import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import get from 'lodash/get';
import { API_RESULT } from '../../modules/api';
import { home, signup } from '../../helpers/routes';
import { track, trackPeople, PEOPLE_SET, PEOPLE_SET_ONCE, PEOPLE_INCREMENT } from '../../helpers/tracking';
import { ERROR_USER_NOT_FOUND, ERROR_USER_ALREADY_EXISTS } from '../../helpers/trackingStringValues';
import TrackingEvents from '../models/TrackingEvents';
import timerQueue, { EventType } from '../../helpers/TimerQueue';

const STATUS_REGISTER_SUCCESS = '1';
const STATUS_USER_ALREADY_EXISTS = '3';
const STATUS_LOGIN_SUCCESS = '4';
const STATUS_LOGIN_NO_USER = '5';

const login = (email, password, { redirect, reload } = { redirect: true, reload: true }, redirectUrl = false) => {
  return (dispatch, getState) => {
    axios.post('/api/authentication', { email, password }).then(response => {
      const { data } = response;
      const { countryCode } = getState();
      dispatch({ payload: data, type: API_RESULT });
      const { authStatus } = data;
      const responseRedirectUrl = get(data, 'redirectUrl', null);
      // Need window.location.href cause nextJs Router won't hit server
      if (authStatus === STATUS_LOGIN_SUCCESS) {
        mixpanel.alias(email);
        mixpanel.identify();
        const date = new Date();
        timerQueue.addEvent({
          callback: () => trackPeople(PEOPLE_SET, 'last login date', { property_string_value: date.toISOString() }),
          type: EventType.UserEvent,
        });
        timerQueue.addEvent({
          callback: () => trackPeople(PEOPLE_SET_ONCE, '$email', { property_string_value: email }),
          type: EventType.UserEvent,
        });
        timerQueue.addEvent({
          callback: () => trackPeople(PEOPLE_INCREMENT, 'number of logins', { property_float_value: 1 }),
          type: EventType.UserEvent,
        });

        timerQueue.addEvent({
          callback: () => track(TrackingEvents.userSignsIn()),
          type: EventType.UserEvent,
        });

        if (reload) {
          timerQueue.addEvent({
            callback: () => window.location.reload(),
            type: EventType.Navigation,
          });
        } else if (redirectUrl || responseRedirectUrl) {
          timerQueue.addEvent({
            callback: () => (window.location.href = redirectUrl || responseRedirectUrl),
            type: EventType.Navigation,
          });
        } else {
          timerQueue.addEvent({
            callback: () => (window.location.href = `/${countryCode}`),
            type: EventType.Navigation,
          });
        }
      }
      if (redirect && authStatus === STATUS_LOGIN_NO_USER) {
        track(TrackingEvents.signinErrorViewed(), { ui: { error: ERROR_USER_NOT_FOUND } });
        window.location.href = `/${countryCode}${signup}`;
      }
    });
  };
};

const register = (email, password, firstName, lastName, marketing, redirectUrl = false) => {
  return (dispatch, getState) => {
    axios.post('/api/register', { email, password, firstName, lastName, marketing }).then(response => {
      const { data } = response;
      const { countryCode } = getState();
      dispatch({ payload: data, type: API_RESULT });
      const { authStatus } = data;
      // Need window.location.href cause nextJs Router won't hit server
      if (authStatus === STATUS_REGISTER_SUCCESS) {
        mixpanel.alias(email);
        mixpanel.identify();
        const date = new Date();
        timerQueue.addEvent({
          callback: () => trackPeople(PEOPLE_SET_ONCE, '$email', { property_string_value: email }),
          type: EventType.UserEvent,
        });
        timerQueue.addEvent({
          callback: () => trackPeople(PEOPLE_SET_ONCE, 'signup date', { property_string_value: date.toISOString() }),
          type: EventType.UserEvent,
        });
        timerQueue.addEvent({
          callback: () =>
            trackPeople(PEOPLE_SET_ONCE, 'first login date', { property_string_value: date.toISOString() }),
          type: EventType.UserEvent,
        });

        timerQueue.addEvent({
          callback: () =>
            track(TrackingEvents.userSignsUp(), {
              user: {
                email,
                optedIn: marketing,
              },
            }),
          type: EventType.UserEvent,
        });
        timerQueue.addEvent({
          callback: () => (window.location.href = redirectUrl || `/${countryCode}`),
          type: EventType.Navigation,
        });
      } else if (authStatus === STATUS_USER_ALREADY_EXISTS) {
        track(TrackingEvents.signupErrorViewed(), { ui: { error: ERROR_USER_ALREADY_EXISTS } });
      }
    });
  };
};

const recoverPassword = email => {
  return dispatch => {
    axios.post('/api/recover_password', { email }).then(response => {
      const { data } = response;
      dispatch({ payload: data, type: API_RESULT });
    });
  };
};

const resetPassword = (password, password_recovery_code) => {
  return dispatch => {
    axios.post('/api/reset_password', { password, password_recovery_code }).then(response => {
      const { data } = response;
      dispatch({ payload: data, type: API_RESULT });
    });
  };
};

const logout = (email, reload = true) => {
  return (dispatch, getState) => {
    axios.delete('/api/authentication').then(response => {
      const { countryCode } = getState();
      dispatch({ payload: response.data, type: API_RESULT });
      timerQueue.addEvent({
        callback: () => track(TrackingEvents.userSignsOut(), { user: { email } }),
        type: EventType.UserEvent,
      });
      if (reload) {
        timerQueue.addEvent({
          callback: () => window.location.reload(),
          type: EventType.Navigation,
        });
      } else {
        timerQueue.addEvent({
          callback: () => (window.location.href = `/${countryCode}`),
          type: EventType.Navigation,
        });
      }
    });
  };
};

export {
  login,
  logout,
  register,
  recoverPassword,
  resetPassword,
  STATUS_LOGIN_SUCCESS,
  STATUS_USER_ALREADY_EXISTS,
  STATUS_LOGIN_NO_USER,
};
